import { INotificationTreatment } from '@pushly/aqe/lib/interfaces/notification-treatment'
import { NotificationDto } from '../features/notifications/dtos/notification-dto'
import { OrgNotificationModel } from '../models/notification/org-notification.model'
import { generate as generateId } from 'randomstring'
import { Platform } from '@pushly/cuttlefish'
import { INotificationAction } from '@pushly/aqe/lib/interfaces/notification-action'

/**
 * Extracts notification summary content for the given platform.
 * When no platform is provided, then the web template content
 * will be used by default.
 */
export function extractSummaryNotification(
    notification: NotificationDto | OrgNotificationModel,
    platform?: Platform,
): INotificationTreatment {
    let id: string | undefined
    let title: string
    let body: string | undefined
    let icon: string | undefined | null
    let image: string | undefined | null
    let badge: string | undefined | null
    let landingUrl: string
    let altNativeUrl: string | undefined | null
    let actions: INotificationAction[] = []
    let keywords: string[] = []
    let subtitle: string | undefined

    // When platform is not defined, web content will be used as the default
    const isPlatformWeb = !platform || [Platform.MAC_OS, Platform.WINDOWS, Platform.ANDROID].includes(platform)
    const isPlatformIos = !isPlatformWeb && platform === Platform.IOS
    const isPlatformAndroid = !isPlatformWeb && platform === Platform.NATIVE_ANDROID

    if (notification instanceof OrgNotificationModel) {
        const contentWrapper = notification.getVariant()?.getContent()
        const defaultContent = contentWrapper?.getDefaultContent()
        const platformContent =
            (isPlatformWeb
                ? contentWrapper?.getWebContent()
                : isPlatformIos
                ? contentWrapper?.getNativeIosContent()
                : isPlatformAndroid
                ? contentWrapper?.getNativeAndroidContent()
                : defaultContent) ?? defaultContent

        id = notification.getId()?.toString()
        title = platformContent?.getTitle() ?? defaultContent?.getTitle() ?? ''
        body = platformContent?.getBody() ?? defaultContent?.getBody() ?? ''
        icon = platformContent?.getIconUrl() ?? defaultContent?.getIconUrl()
        image = platformContent?.getImageUrl() ?? defaultContent?.getImageUrl()
        landingUrl =
            (platformContent && 'getLandingUrl' in platformContent
                ? platformContent.getLandingUrl()
                : defaultContent?.getLandingUrl()) ??
            defaultContent?.getLandingUrl() ??
            ''
        altNativeUrl = defaultContent?.getNativeAltUrl()

        if (platformContent && 'getSubtitle' in platformContent) {
            subtitle = platformContent.getSubtitle()
        }

        badge = defaultContent?.getBadgeUrl()
        actions = defaultContent?.getActions()?.map((a) => a.serialize()) ?? []
        keywords = defaultContent?.getKeywords() ?? []
    } else {
        const defaultContent = notification.defaultTemplate
        const platformContent =
            (isPlatformWeb
                ? notification.webTemplate
                : isPlatformIos
                ? notification.nativeIosTemplate
                : isPlatformAndroid
                ? notification.nativeAndroidTemplate
                : defaultContent) ?? defaultContent

        id = notification.id?.toString()
        title = platformContent.title ?? defaultContent.title ?? ''
        body = platformContent.body ?? defaultContent.body ?? ''
        icon = platformContent.iconUrl ?? defaultContent.iconUrl
        image = platformContent.imageUrl ?? defaultContent.imageUrl
        landingUrl = platformContent.landingUrl ?? defaultContent.landingUrl ?? ''
        altNativeUrl = defaultContent.altNativeUrl || undefined

        if ('subtitle' in platformContent) {
            subtitle = platformContent.subtitle
        }

        badge =
            ('badgeUrl' in platformContent ? platformContent.badgeUrl : defaultContent.badgeUrl) ??
            defaultContent.badgeUrl

        actions =
            ('actions' in platformContent && platformContent.actions?.length
                ? platformContent.actions
                : defaultContent.actions) ??
            defaultContent.actions ??
            []

        keywords = notification.keywords
    }

    return {
        id: id ?? generateId({ length: 5 }),
        title,
        subtitle,
        body,
        image: image ?? undefined,
        icon: icon ?? undefined,
        badge: badge ?? undefined,
        altNativeUrl: altNativeUrl ?? undefined,
        landingUrl,
        landingUrlDisabled: !landingUrl,
        actions,
        keywords,
    }
}
